import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { register } from '../../store/slices/authSlice';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import {
  Container, Box, Typography, TextField, Button, Card, CardContent,
  InputAdornment, IconButton, CircularProgress, Alert, Grid, MenuItem
} from '@mui/material';
import { Person, Email, Lock, Visibility, VisibilityOff, Phone, Business, Language as LanguageIcon } from '@mui/icons-material';

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, loading, error } = useSelector(state => state.auth);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', password: '',
    confirmPassword: '', phone: '', company: '', country: '',
    language: i18n.language || 'en'
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate('/customer');
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (formData.password !== formData.confirmPassword) {
      setFormError('Passwords do not match'); return;
    }
    if (formData.password.length < 8) {
      setFormError('Password must be at least 8 characters'); return;
    }
    const { confirmPassword, ...data } = formData;
    if (data.language) {
      try { i18n.changeLanguage(data.language); } catch (_) {}
    }
    dispatch(register(data));
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Card elevation={4} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Typography variant="h4" fontWeight={800} color="primary">OSESG</Typography>
            <Typography variant="h6" color="text.secondary">{t('register')}</Typography>
            <Typography variant="body2" color="text.secondary">Create your free buyer account</Typography>
          </Box>

          {(error || formError) && <Alert severity="error" sx={{ mb: 2 }}>{formError || error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label={t('first_name')} name="firstName" required
                  value={formData.firstName} onChange={handleChange}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={t('last_name')} name="lastName" required
                  value={formData.lastName} onChange={handleChange} />
              </Grid>
            </Grid>

            <TextField fullWidth label={t('email')} name="email" type="email" required sx={{ mt: 2 }}
              value={formData.email} onChange={handleChange}
              InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }} />

            <TextField fullWidth label={t('phone')} name="phone" sx={{ mt: 2 }}
              value={formData.phone} onChange={handleChange}
              InputProps={{ startAdornment: <InputAdornment position="start"><Phone /></InputAdornment> }} />

            <TextField fullWidth label="Company Name" name="company" sx={{ mt: 2 }}
              value={formData.company} onChange={handleChange}
              InputProps={{ startAdornment: <InputAdornment position="start"><Business /></InputAdornment> }} />

            <TextField fullWidth label="Country" name="country" sx={{ mt: 2 }}
              value={formData.country} onChange={handleChange} />

            <TextField select fullWidth label={t('language') || 'Language'} name="language" required sx={{ mt: 2 }}
              value={formData.language} onChange={handleChange}
              InputProps={{ startAdornment: <InputAdornment position="start"><LanguageIcon /></InputAdornment> }}>
              {SUPPORTED_LANGUAGES.map(l => (
                <MenuItem key={l.code} value={l.code}>
                  <span style={{ marginRight: 8 }}>{l.flag}</span>
                  {l.nativeLabel} ({l.label})
                </MenuItem>
              ))}
            </TextField>

            <TextField fullWidth label={t('password')} name="password" required sx={{ mt: 2 }}
              type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                endAdornment: <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }} />

            <TextField fullWidth label="Confirm Password" name="confirmPassword" required sx={{ mt: 2 }}
              type="password" value={formData.confirmPassword} onChange={handleChange}
              InputProps={{ startAdornment: <InputAdornment position="start"><Lock /></InputAdornment> }} />

            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}
              sx={{ mt: 3, py: 1.5, fontSize: '1rem' }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : t('register')}
            </Button>
          </Box>

          <Box textAlign="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Typography component={Link} to="/login" variant="body2" color="primary" fontWeight={600}>
                {t('login')}
              </Typography>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default RegisterPage;
